import { useEffect, useState } from 'react';
import { Modal, Field, Spinner } from './ui';
import { newsApi, storyCategoriesApi, eventsApi } from '../api/resources';
import { formatDate } from '../lib/format';

const TARGETS = [
  { key: 'activity', label: 'Activity', hint: 'A service project or Area activity.' },
  { key: 'story', label: 'Story', hint: 'A member story, optionally in a category.' },
  { key: 'event', label: 'New Event', hint: 'Creates an event — you set the date and place.' },
  { key: 'event_recap', label: 'Recap of an existing Event', hint: 'Attaches the text and photos to an event that already exists.' },
];

const TYPE_LABEL = { activity: 'Activity', story: 'Story', event: 'Event', event_recap: 'Event recap' };

/**
 * "Move to…" dialog for an Admin News row (design spec 2026-09-14 §4).
 * Props: { news, onClose, onMoved({ type, item, event, message }) }
 */
export default function MoveNewsModal({ news, onClose, onMoved }) {
  const [target, setTarget] = useState('activity');
  const [categoryId, setCategoryId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [location, setLocation] = useState('');
  const [eventId, setEventId] = useState('');
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!news) return;
    setTarget('activity');
    setCategoryId('');
    setStartsAt('');
    setEndsAt('');
    setLocation('');
    setEventId('');
    setError(null);
    setFieldErrors({});
    storyCategoriesApi.list().then(setCategories).catch(() => setCategories([]));
    eventsApi.list({ per_page: 100 }).then((r) => setEvents(r.items || [])).catch(() => setEvents([]));
  }, [news]);

  const valid =
    target === 'activity' || target === 'story' || (target === 'event' && startsAt) || (target === 'event_recap' && eventId);

  async function submit() {
    if (!news || !valid) return;
    setBusy(true);
    setError(null);
    setFieldErrors({});
    const payload = { target };
    if (target === 'story' && categoryId) payload.story_category_id = Number(categoryId);
    if (target === 'event') {
      payload.starts_at = startsAt;
      if (endsAt) payload.ends_at = endsAt;
      if (location.trim()) payload.location = location.trim();
    }
    if (target === 'event_recap') payload.event_id = Number(eventId);

    try {
      const result = await newsApi.move(news.slug || news.id, payload);
      onMoved?.(result);
    } catch (err) {
      setError(err?.message || 'Could not move this post.');
      setFieldErrors(err?.errors || {});
    } finally {
      setBusy(false);
    }
  }

  const firstError = (key) => (Array.isArray(fieldErrors[key]) ? fieldErrors[key][0] : fieldErrors[key]);

  return (
    <Modal
      open={!!news}
      onClose={busy ? undefined : onClose}
      title="Move news post"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={submit} className="btn-primary" disabled={busy || !valid}>
            {busy ? <Spinner className="h-4 w-4" /> : 'Move'}
          </button>
        </>
      }
    >
      {news && (
        <div className="space-y-5">
          <p className="text-sm text-ink-soft">
            Move <span className="font-semibold text-ink">“{news.title}”</span> out of News. Its text, photos and
            Facebook link go with it; the news post itself is removed.
          </p>

          <fieldset className="space-y-2">
            <legend className="label">Move to</legend>
            {TARGETS.map((t) => (
              <label
                key={t.key}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 text-sm transition ${
                  target === t.key ? 'border-maroon-400 bg-maroon-50' : 'border-cream-300 hover:bg-surface-alt'
                }`}
              >
                <input
                  type="radio"
                  name="move-target"
                  value={t.key}
                  checked={target === t.key}
                  onChange={() => setTarget(t.key)}
                  className="mt-1 accent-maroon-700"
                />
                <span>
                  <span className="block font-semibold text-ink">{t.label}</span>
                  <span className="block text-xs text-ink-muted">{t.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {target === 'story' && (
            <Field label="Category" hint="Optional." error={firstError('story_category_id')}>
              <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">No category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {target === 'event' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Starts at" error={firstError('starts_at')}>
                <input type="datetime-local" className="input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required />
              </Field>
              <Field label="Ends at" hint="Optional." error={firstError('ends_at')}>
                <input type="datetime-local" className="input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Location" hint="Optional." error={firstError('location')}>
                  <input type="text" className="input" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={255} />
                </Field>
              </div>
            </div>
          )}

          {target === 'event_recap' && (
            <Field label="Event" error={firstError('event_id')}>
              <select className="input" value={eventId} onChange={(e) => setEventId(e.target.value)} required>
                <option value="">Choose an event…</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}
                    {ev.starts_at ? ` — ${formatDate(ev.starts_at)}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {error && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

export { TYPE_LABEL };
